import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { GameView } from '@wordloom/engine';
import { dictCache } from '../game/dict';
import type { DraftPlacement } from '../game/draft';
import { evaluateDraft, summarizePreview, type PreviewModel } from '../game/preview';
import { useRoomStore } from '../state/roomStore';

const subscribe = (fn: () => void) => dictCache.subscribe(fn);
const snapshot = () => dictCache.version;

/**
 * The live move preview: instant local evaluation, then (debounced, cached)
 * dictionary verdicts for the formed words.
 */
export function usePreview(game: GameView | null, placements: readonly DraftPlacement[]): PreviewModel {
  const dictVersion = useSyncExternalStore(subscribe, snapshot, snapshot);
  const evaluation = useMemo(
    () => (game ? evaluateDraft(game.board, placements, game.rack) : null),
    [game, placements],
  );

  // Ask the server about any formed word we have not seen yet — and again
  // once the seat is back after a drop: a failed lookup is not cached, so the
  // words it left 'checking' are re-asked (request() skips known and in-flight
  // words, so the re-run is cheap).
  const words = evaluation?.words;
  const wordKey = words ? words.map((w) => w.word).join(',') : '';
  // Same readiness as the dict:check request itself (a joined seat).
  const online = useRoomStore((s) => s.connection === 'connected' && s.session?.joined === true);
  useEffect(() => {
    if (wordKey && online) dictCache.request(wordKey.split(','));
  }, [wordKey, online]);

  return useMemo(
    () => summarizePreview(evaluation, (w) => dictCache.status(w)),
    // dictVersion is not read inside, but a new verdict must re-summarize.
    [evaluation, dictVersion],
  );
}
